import { Badge, Currency, Table } from "@xola/ui-kit";
import { DateFormat, formatDate } from "common";
import React from "react";
import { DeliveryReport } from "types";
import { PhoneWithTooltip } from "./PhoneWithTooltip";
import { InfiniteList } from "@/components/Common/InfiniteList";
import { NoResults } from "@/components/Common/NoResults";
import { TableSkeleton } from "@/components/Common/Skeleton/TableSkeleton";
import {
    getCreditsLabel,
    getPhoneDisplay,
    getStatusColor,
    getStatusIcon,
    getStatusLabel,
} from "@/models/DeliveryReport";

interface MessageDeliveryTableProps {
    reports: DeliveryReport[];
    isLoading: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    onSelectReport: (report: DeliveryReport) => void;
}

export const MessageDeliveryTable = ({
    reports,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    onLoadMore,
    onSelectReport,
}: MessageDeliveryTableProps) => {
    if (isLoading) {
        return <TableSkeleton rows={5} columns={5} />;
    }

    if (reports.length === 0) {
        return <NoResults>No text messages found</NoResults>;
    }

    return (
        <InfiniteList isLoading={isFetchingNextPage} hasMore={hasNextPage} onLoadMore={onLoadMore}>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header>To</Table.Header>
                        <Table.Header>Status</Table.Header>
                        <Table.Header>Credits</Table.Header>
                        <Table.Header>Cost</Table.Header>
                        <Table.Header>Last Updated</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped>
                    {reports.map((report) => (
                        <MessageDeliveryRow key={report.id} report={report} onSelectReport={onSelectReport} />
                    ))}
                </Table.Body>
            </Table>
        </InfiniteList>
    );
};

interface MessageDeliveryRowProps {
    report: DeliveryReport;
    // Forwarded from `Table.Body isStriped`, or the striping class silently never reaches the `<tr>`.
    isStriped?: boolean;
    onSelectReport: (report: DeliveryReport) => void;
}

const MessageDeliveryRow = ({ report, isStriped, onSelectReport }: MessageDeliveryRowProps) => {
    const { status, credits, cost, error, updatedAt } = report;
    const sentTo = getPhoneDisplay(report.sentTo, report.sentToNormalized);
    const StatusIcon = getStatusIcon(status);

    const handleClick = () => onSelectReport(report);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelectReport(report);
        }
    };

    return (
        <Table.Row
            isStriped={isStriped}
            tabIndex={0}
            role="button"
            aria-label={`View delivery details for report to ${sentTo?.title ?? "unknown recipient"}`}
            className="cursor-pointer hover:bg-gray-lighter"
            data-testid="message-delivery-row"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <Table.Cell className="no-translate tabular-nums">
                <PhoneWithTooltip phone={sentTo} fallback="--" />
                {error && <div className="text-sm text-danger-dark">{error}</div>}
            </Table.Cell>

            <Table.Cell>
                <Badge size="small" color={getStatusColor(status)} icon={<StatusIcon />}>
                    {getStatusLabel(status)}
                </Badge>
            </Table.Cell>

            <Table.Cell className="tabular-nums" data-testid="message-delivery-row-credits">
                {getCreditsLabel(credits)}
            </Table.Cell>

            <Table.Cell className="no-translate tabular-nums" data-testid="message-delivery-row-cost">
                {/* Always USD: the provider bills Xola in USD, and five reported decimals are not rounded away. */}
                {cost === null ? (
                    "--"
                ) : (
                    <Currency
                        currency="USD"
                        locale="en-US"
                        maximumFractionDigits={5}
                        shouldRemoveTrailingZeroes={false}
                    >
                        {cost}
                    </Currency>
                )}
            </Table.Cell>

            <Table.Cell className="no-translate tabular-nums">
                {formatDate(updatedAt, DateFormat.DATE_TIME_SHORT)}
            </Table.Cell>
        </Table.Row>
    );
};
